import {
  Button,
  DropdownInput,
  FlexBox,
  JoonUIColor,
  QuestRoutine,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import { CloseButton } from "../../../../components/closeButton/CloseButton"
import SlideUpModal from "../../../../components/slideUpModal/SlideUpModal"
import { useAddEditQuestStore } from "../useAddEditQuestStore"

interface SetTimeModalStoreProps {
  questRoutine: QuestRoutine | null
  isOpen: boolean
  onOpen: (questRoutine: QuestRoutine) => void
  onClose: () => void
}

export const useSetTimeModalStore = create<SetTimeModalStoreProps>()((set) => ({
  questRoutine: null,
  isOpen: false,
  onOpen: (questRoutine: QuestRoutine) => set({ questRoutine, isOpen: true }),
  onClose: () => set({ isOpen: false }),
}))

const hourOptions = Array.from({ length: 12 }, (_, i) => ({
  value: `${i + 1}`,
  label: `${i + 1}`,
}))

const minuteOptions = Array.from({ length: 60 }, (_, i) => ({
  value: `${i}`.padStart(2, "0"),
  label: `${i}`.padStart(2, "0"),
}))

export default function SetTimeModal() {
  const { reminderTimeMap, setReminderTimeMap, setRoutine } =
    useAddEditQuestStore()
  const { isOpen, onClose, questRoutine } = useSetTimeModalStore()
  const { t } = useTranslation()

  const initialHour =
    questRoutine && reminderTimeMap[questRoutine]
      ? reminderTimeMap[questRoutine]?.split(":")[0]
      : "9"
  const initialMinute =
    questRoutine && reminderTimeMap[questRoutine]
      ? reminderTimeMap[questRoutine]?.split(":")[1]?.split(" ")[0]
      : "00"
  const initialAmPm =
    questRoutine && reminderTimeMap[questRoutine]
      ? reminderTimeMap[questRoutine]?.split(":")[1]?.split(" ")[1]
      : "AM"

  const [hour, setHour] = useState<any>({
    value: initialHour,
    label: initialHour,
  })
  const [minute, setMinute] = useState<any>({
    value: initialMinute,
    label: initialMinute,
  })
  const [amPm, setAmPm] = useState<any>({
    value: initialAmPm,
    label: initialAmPm,
  })

  useEffect(() => {
    if (isOpen && questRoutine) {
      setHour({ value: initialHour, label: initialHour })
      setMinute({ value: initialMinute, label: initialMinute })
      setAmPm({ value: initialAmPm, label: initialAmPm })
    }
  }, [questRoutine, isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  const onDone = () => {
    let timeString = `${hour.value}:${minute.value} ${amPm.value}`
    if (questRoutine) {
      setReminderTimeMap({
        [questRoutine]: timeString,
      })
      setRoutine(questRoutine)
    }
    onClose()
  }

  return (
    <SlideUpModal
      isOpen={isOpen}
      onClose={onClose}
      size="fit-content"
      backgroundType="overlay"
      zIndex={1000}
    >
      <FlexBox
        direction="column"
        justify="space-between"
        align="center"
        wrap={false}
        fullWidth
        gap={SPACING.space4}
        style={{
          position: "relative",
          background: JoonUIColor.background.xlightGray,
          paddingTop: SPACING.space4,
          paddingBottom: SPACING.space3,
          padding: SPACING.space4,
        }}
      >
        <FlexBox direction="column" gap={SPACING.space4}>
          <CloseButton onClick={onClose} />
          <Typography variant="bodyBold" textAlign="left">
            {t("QUEST_SET_DUE_TIME")}
          </Typography>
          <FlexBox
            direction="row"
            justify="center"
            align="center"
            gap={SPACING.space2}
            wrap={false}
            fullWidth
          >
            <DropdownInput
              name="set-time-hour"
              options={hourOptions}
              label={hour?.label}
              onSelect={setHour}
              placeholder={t("QUEST_HOUR")}
              style={{ background: JoonUIColor.background.primaryNeutral }}
              fullWidth
            />
            <DropdownInput
              name="set-time-minute"
              options={minuteOptions}
              label={minute?.label}
              onSelect={setMinute}
              placeholder={t("QUEST_MINUTE_PH")}
              style={{ background: JoonUIColor.background.primaryNeutral }}
              fullWidth
            />
            <DropdownInput
              name="set-time-am-pm"
              options={["AM", "PM"].map((option) => ({
                value: option,
                label: option,
              }))}
              label={amPm?.label}
              onSelect={setAmPm}
              placeholder={t("QUEST_AMPM")}
              style={{ background: JoonUIColor.background.primaryNeutral }}
              fullWidth
            />
          </FlexBox>
        </FlexBox>

        <Button text={t("QUEST_DONE")} onClick={onDone} fullWidth />
      </FlexBox>
    </SlideUpModal>
  )
}
